/**
 * GuidedConversationModal — Linear, calm, premium guided alignment experience.
 *
 * Flow: Entry → Prompt 1 → Prompt 2 → Prompt 3 → Save Step → Completion
 *
 * Tier-based prompts (CORE / RITUAL / LEGACY) — rule-based, no AI.
 * Pulls categories from results.category_intelligence (Page 2 derived).
 * Saves agreement + insight via POST /api/v2/relationships/{id}/guided-conversation/save-step
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, ArrowRight, ArrowLeft, Loader2, CheckCircle, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Tier-based prompts (verbatim from spec) ─────────────────────────────────
const TIER_PROMPTS = {
  CORE: {
    label: 'CORE · Clarity & Awareness',
    p1: {
      heading: 'Awareness',
      context: 'You both value this area of your relationship.',
      question: 'What has felt most aligned between you recently?',
    },
    p2: {
      heading: 'Appreciation',
      context: 'There is a shared strength here.',
      question: 'What is one thing you appreciate about how your partner shows up for you?',
    },
    p3: {
      heading: 'Light Growth',
      context: 'There is an opportunity to grow together.',
      question: 'What is one small way you could support each other better this week?',
    },
    save: 'What is one thing you now understand better about each other?',
  },
  RITUAL: {
    label: 'RITUAL · Connection & Pattern Awareness',
    p1: {
      heading: 'Pattern Awareness',
      context: 'You both experience connection here, but in different ways.',
      question: 'When do you feel most connected to each other—and what’s happening in those moments?',
    },
    p2: {
      heading: 'Needs Expression',
      context: 'There may be unspoken expectations here.',
      question: 'What is something you need more of from your partner that hasn’t been clearly expressed?',
    },
    p3: {
      heading: 'Intentional Action',
      context: 'There is an opportunity to strengthen this area.',
      question: 'What is one intentional action you can take this week to deepen your connection?',
    },
    save: 'What is one agreement or intention you want to carry forward from this conversation?',
  },
  LEGACY: {
    label: 'LEGACY · Transformation & Design',
    p1: {
      heading: 'Truth',
      context: 'There is something meaningful beneath the surface here.',
      question: 'What is one truth about your relationship that may not be said often—but is felt?',
    },
    p2: {
      heading: 'Vision',
      context: 'You are shaping something together.',
      question: 'What kind of relationship are you actively choosing to create?',
    },
    p3: {
      heading: 'Agreement',
      context: 'This is an opportunity for conscious alignment.',
      question: 'What is one clear agreement you can make today that reflects the relationship you want to build?',
    },
    save: 'What is one conscious agreement you are choosing to honor moving forward?',
  },
};

// ── Pick the most relevant category from category_intelligence ──────────────
function pickPrimaryCategory(categories) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return { category: 'Your Relationship', alignment_level: 'medium', summary: '', strength: '', tension: '', growth_opportunity: '' };
  }
  const order = { low: 0, medium: 1, high: 2 };
  return [...categories].sort((a, b) => {
    const la = order[a.alignment_level] ?? 1;
    const lb = order[b.alignment_level] ?? 1;
    if (la !== lb) return la - lb;
    return (a.alignment_score ?? 100) - (b.alignment_score ?? 100);
  })[0];
}

// ── UI primitives ───────────────────────────────────────────────────────────
const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#888888]">
    {children}
  </p>
);

const StepDots = ({ total, current }) => (
  <div className="flex items-center justify-center gap-1.5" data-testid="step-dots">
    {Array.from({ length: total }).map((_, i) => (
      <span
        key={i}
        className={
          'h-1.5 rounded-full transition-all ' +
          (i === current
            ? 'w-6 bg-mc-blue-deep'
            : i < current
              ? 'w-1.5 bg-mc-blue-deep'
              : 'w-1.5 bg-[#D8D4C8]')
        }
      />
    ))}
  </div>
);

const PrimaryButton = ({ children, onClick, disabled, testid }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="inline-flex items-center justify-center gap-2 rounded-full bg-mc-blue-deep px-6 py-3 text-sm font-medium text-white transition-all hover:bg-mc-blue-dark active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#E5E2D8] bg-white px-6 py-3 text-sm font-medium text-[#444444] transition-all hover:bg-[#F4F3EE]"
  >
    {children}
  </button>
);

// ── Main component ──────────────────────────────────────────────────────────
export default function GuidedConversationModal({
  open,
  onClose,
  relationshipId,
  token,
  tier = 'CORE',
  categoryIntelligence = [],
  onSaved,
}) {
  const [step, setStep] = useState(0); // 0 entry, 1-3 prompts, 4 save, 5 complete
  const [insight, setInsight] = useState('');
  const [agreement, setAgreement] = useState('');
  const [saving, setSaving] = useState(false);

  const primary = useMemo(() => pickPrimaryCategory(categoryIntelligence), [categoryIntelligence]);
  const tierPrompts = TIER_PROMPTS[tier] || TIER_PROMPTS.CORE;

  useEffect(() => {
    if (open) {
      setStep(0);
      setInsight('');
      setAgreement('');
      setSaving(false);
    }
  }, [open]);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!relationshipId || !token) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/v2/relationships/${relationshipId}/guided-conversation/save-step`,
        {
          token,
          tier,
          category: primary.category,
          insight,
          agreement,
          source: 'guided_conversation',
        }
      );
      setStep(5);
      if (onSaved) onSaved();
    } catch (e) {
      // Even if save fails we still close gracefully — user reflection is intact
      console.error('save-step failed', e);
      setStep(5);
    } finally {
      setSaving(false);
    }
  };

  const renderEntry = () => (
    <div className="space-y-6">
      <Eyebrow>Guided Alignment Experience</Eyebrow>
      <h2
        className="font-serif text-[30px] leading-[1.18] text-[#111111] sm:text-[36px]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        A short, intentional conversation — together.
      </h2>
      <p className="text-[15px] leading-7 text-[#555555]">
        Three calm prompts followed by a moment to capture what you choose to carry forward. Take your time.
      </p>
      <div className="rounded-xl border border-[#E5E2D8] bg-[#F4F3EE] p-5">
        <Eyebrow>Tonight&apos;s focus</Eyebrow>
        <p
          className="mt-2 font-serif text-[20px] leading-snug text-[#111111]"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {primary.category}
        </p>
        {primary.summary ? (
          <p className="mt-2 text-sm leading-6 text-[#666666]">{primary.summary}</p>
        ) : null}
        <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[#6FC7F2]">
          {tierPrompts.label}
        </p>
      </div>
      <div className="flex justify-end">
        <PrimaryButton onClick={() => setStep(1)} testid="modal-begin-btn">
          Begin
          <ArrowRight className="h-4 w-4" />
        </PrimaryButton>
      </div>
    </div>
  );

  const renderPrompt = (n) => {
    const p = tierPrompts[`p${n}`];
    return (
      <div className="space-y-6" data-testid={`modal-prompt-${n}`}>
        <div className="flex items-center justify-between">
          <Eyebrow>Prompt {n} of 3 · {p.heading}</Eyebrow>
          <StepDots total={5} current={n} />
        </div>
        <p className="text-sm leading-6 text-[#666666]">{p.context}</p>
        <p
          className="font-serif text-[26px] leading-snug text-[#111111] sm:text-[30px]"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {p.question}
        </p>
        <p className="text-xs italic text-[#888888]">
          Speak it together. There is no input here — this is a moment to share, not type.
        </p>
        <div className="flex items-center justify-between pt-4">
          <SecondaryButton
            onClick={() => setStep(Math.max(0, n - 1))}
            testid={`modal-back-${n}`}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </SecondaryButton>
          <PrimaryButton
            onClick={() => setStep(n + 1)}
            testid={`modal-next-${n}`}
          >
            {n < 3 ? 'Next' : 'Save what we choose to carry forward'}
            <ArrowRight className="h-4 w-4" />
          </PrimaryButton>
        </div>
      </div>
    );
  };

  const renderSave = () => (
    <div className="space-y-6" data-testid="modal-save-step">
      <div className="flex items-center justify-between">
        <Eyebrow>Save your reflection</Eyebrow>
        <StepDots total={5} current={4} />
      </div>
      <p
        className="font-serif text-[24px] leading-snug text-[#111111]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        {tierPrompts.save}
      </p>
      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#888888]">
            What you now understand
          </label>
          <textarea
            value={insight}
            onChange={(e) => setInsight(e.target.value)}
            data-testid="modal-insight-input"
            className="mt-2 w-full rounded-lg border border-[#E5E2D8] bg-white p-3 text-sm leading-6 text-[#111111] focus:border-[#6FC7F2] focus:outline-none focus:ring-2 focus:ring-[#6FC7F2]/30"
            rows={3}
            placeholder="A reflection or realization to carry forward…"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#888888]">
            One agreement (optional)
          </label>
          <textarea
            value={agreement}
            onChange={(e) => setAgreement(e.target.value)}
            data-testid="modal-agreement-input"
            className="mt-2 w-full rounded-lg border border-[#E5E2D8] bg-white p-3 text-sm leading-6 text-[#111111] focus:border-[#6FC7F2] focus:outline-none focus:ring-2 focus:ring-[#6FC7F2]/30"
            rows={2}
            placeholder="e.g. We will check in weekly about emotional support."
          />
        </div>
      </div>
      <div className="flex items-center justify-between pt-2">
        <SecondaryButton onClick={() => setStep(3)} testid="modal-back-save">
          <ArrowLeft className="h-4 w-4" />
          Back
        </SecondaryButton>
        <PrimaryButton
          onClick={handleSave}
          disabled={saving || (!insight.trim() && !agreement.trim())}
          testid="modal-save-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              Save this step
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </PrimaryButton>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="space-y-6 text-center" data-testid="modal-complete">
      <CheckCircle className="mx-auto h-10 w-10 text-[#6FC7F2]" />
      <h2
        className="font-serif text-[28px] leading-[1.2] text-[#111111] sm:text-[34px]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        You didn&apos;t just reflect — you created clarity together.
      </h2>
      <p className="mx-auto max-w-md text-[15px] leading-7 text-[#555555]">
        This insight is now part of your Alignment Intelligence™. This is how alignment is built — one honest conversation at a time.
      </p>
      <div className="flex flex-col items-center gap-3 pt-2 sm:flex-row sm:justify-center">
        <PrimaryButton
          onClick={() => {
            onClose();
            window.location.assign(`/alignment-intelligence/${relationshipId}?token=${token}`);
          }}
          testid="modal-explore-intelligence-btn"
        >
          <Sparkles className="h-4 w-4" />
          Explore Your Alignment Intelligence™
        </PrimaryButton>
        <SecondaryButton onClick={onClose} testid="modal-close-complete">
          Return to Results
        </SecondaryButton>
      </div>
    </div>
  );

  let body;
  if (step === 0) body = renderEntry();
  else if (step >= 1 && step <= 3) body = renderPrompt(step);
  else if (step === 4) body = renderSave();
  else body = renderComplete();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="guided-conversation-backdrop"
    >
      <div
        className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-[#E5E2D8] bg-[#FAFAF8] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="guided-conversation-modal"
      >
        <div className="flex items-center justify-between border-b border-[#EFEDE6] px-6 py-3">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-[#6FC7F2]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#888888]">
              Guided Conversation
            </span>
          </div>
          <button
            onClick={onClose}
            data-testid="modal-close-btn"
            className="rounded-full p-1.5 text-[#888888] transition-colors hover:bg-[#EFEDE6] hover:text-[#111111]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-8 sm:px-10 sm:py-10">{body}</div>
      </div>
    </div>
  );
}
